/**
 * MeekoChildrenSync — Admin : synchronisation enfants Meeko → SuiviPlus
 * Selecteur de structure + preview + confirmation
 */

import { useState, useEffect } from 'react';
import { RefreshCw, Check, Plus, ArrowRight, Loader, AlertCircle, Users } from 'lucide-react';
import { previewMeekoSync, confirmMeekoSync, testMeekoConnection } from '../../api';
import { useStructure } from '../../contexts/StructureContext';
import { useToast } from '../../contexts/NotificationContext';

const STATUS_LABELS = {
  pre_inscription: { label: 'Pre-inscription', color: 'bg-orange-100 text-orange-700' },
  inscrit: { label: 'Inscrit', color: 'bg-green-100 text-green-700' },
  a_venir: { label: 'A venir', color: 'bg-purple-100 text-purple-700' },
  sorti: { label: 'Sorti', color: 'bg-gray-100 text-gray-500' },
  manuel: { label: 'Manuel', color: 'bg-blue-100 text-blue-700' },
};

const ACTION_LABELS = {
  create: { label: 'Nouveau', color: 'bg-green-100 text-green-700', icon: Plus },
  update: { label: 'A lier', color: 'bg-blue-100 text-blue-700', icon: ArrowRight },
  unchanged: { label: 'Deja lie', color: 'bg-gray-100 text-gray-500', icon: Check },
};

const MeekoChildrenSync = () => {
  const { structures } = useStructure();
  const toast = useToast();

  const [selectedStructure, setSelectedStructure] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selections, setSelections] = useState({});
  const [connectionOk, setConnectionOk] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  // Test connexion au chargement
  useEffect(() => {
    (async () => {
      setTestingConnection(true);
      try {
        const { data } = await testMeekoConnection();
        setConnectionOk(data);
      } catch {
        setConnectionOk(null);
      }
      setTestingConnection(false);
    })();
  }, []);

  const handlePreview = async () => {
    if (!selectedStructure) { toast.error('Selectionnez une structure'); return; }
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await previewMeekoSync({ structureId: selectedStructure });
      setPreview(data);
      const sel = {};
      data.kids.forEach(k => {
        sel[k.meekoId] = k.action === 'create' || k.action === 'update';
      });
      setSelections(sel);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur connexion Meeko');
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const kids = preview.kids.map(k => ({ ...k, selected: !!selections[k.meekoId] }));
      const { data } = await confirmMeekoSync({ structureId: selectedStructure, kids });
      toast.success(`Synchronisation terminee : ${data.created} cree(s), ${data.updated} mis a jour`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur synchronisation');
    }
    setConfirming(false);
  };

  const toggleAll = (checked) => {
    if (!preview) return;
    const sel = {};
    preview.kids.forEach(k => {
      sel[k.meekoId] = k.action !== 'unchanged' ? checked : false;
    });
    setSelections(sel);
  };

  const selectedCount = Object.values(selections).filter(Boolean).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Users size={20} className="text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">Synchronisation enfants Meeko</h2>
      </div>

      {/* Statut connexion */}
      <div className={`rounded-lg p-3 mb-4 text-sm flex items-center gap-2 ${
        testingConnection ? 'bg-gray-50 text-gray-500' :
        connectionOk ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      }`}>
        {testingConnection ? (
          <><Loader size={14} className="animate-spin" /> Test de connexion Meeko...</>
        ) : connectionOk ? (
          <><Check size={14} /> Connecte a Meeko — {connectionOk.user?.firstName} {connectionOk.user?.lastName} ({connectionOk.nurseries?.length} structure{connectionOk.nurseries?.length > 1 ? 's' : ''})</>
        ) : (
          <><AlertCircle size={14} /> Connexion Meeko non configuree (MEEKO_EMAIL / MEEKO_PASSWORD)</>
        )}
      </div>

      {connectionOk && !preview && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <p className="text-sm text-gray-500">
            Selectionnez une structure puis lancez la synchronisation. Les enfants de Meeko seront compares avec ceux de SuiviPlus. Vous pourrez choisir lesquels importer ou mettre a jour.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Structure</label>
              <select
                value={selectedStructure}
                onChange={e => { setSelectedStructure(e.target.value); setPreview(null); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              >
                <option value="">Selectionner une structure...</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <button
              onClick={handlePreview}
              disabled={loading || !selectedStructure}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {loading ? 'Connexion...' : 'Lancer la synchronisation'}
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-4 mt-4">
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white border rounded-lg p-3 text-center">
              <span className="text-2xl font-bold text-gray-800">{preview.total}</span>
              <p className="text-xs text-gray-500">Total Meeko</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <span className="text-2xl font-bold text-green-600">{preview.toCreate}</span>
              <p className="text-xs text-green-700">Nouveaux</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <span className="text-2xl font-bold text-blue-600">{preview.toUpdate}</span>
              <p className="text-xs text-blue-700">A lier</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <span className="text-2xl font-bold text-gray-500">{preview.unchanged}</span>
              <p className="text-xs text-gray-500">Deja lies</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[450px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-left">
                      <input type="checkbox" checked={selectedCount > 0} onChange={e => toggleAll(e.target.checked)} className="rounded border-gray-300" />
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">Enfant Meeko</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">Naissance</th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600">Contrat</th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600">Statut</th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600">Action</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600">Correspondance SuiviPlus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.kids.map(kid => {
                    const action = ACTION_LABELS[kid.action];
                    const status = STATUS_LABELS[kid.meekoStatus];
                    const ActionIcon = action?.icon;
                    return (
                      <tr key={kid.meekoId} className={`hover:bg-gray-50 ${kid.action === 'unchanged' ? 'opacity-40' : ''}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={!!selections[kid.meekoId]} onChange={e => setSelections(s => ({ ...s, [kid.meekoId]: e.target.checked }))}
                            disabled={kid.action === 'unchanged'} className="rounded border-gray-300" />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{kid.firstName} {kid.lastName}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {kid.birthDate ? new Date(kid.birthDate).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-3 py-2 text-center text-xs text-gray-500">
                          {kid.contractStart ? (
                            <span>{new Date(kid.contractStart).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })} → {kid.contractEnd ? new Date(kid.contractEnd).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '...'}</span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status?.color || 'bg-gray-100'}`}>
                            {status?.label || kid.meekoStatus}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${action?.color || 'bg-gray-100'}`}>
                            {ActionIcon && <ActionIcon size={10} />} {action?.label || kid.action}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">
                          {kid.existingName || <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button onClick={() => setPreview(null)} className="text-sm text-gray-500 hover:text-gray-700">
              ← Retour
            </button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{selectedCount} selectionne(s)</span>
              <button onClick={handleConfirm} disabled={confirming || selectedCount === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm">
                {confirming ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                Confirmer l'import ({selectedCount})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeekoChildrenSync;
